import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import useSWR from 'swr';
import { ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight, Loader2, Play, Square } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HoverCard } from '@/components/ui/hover-card';
import { useT } from '@/contexts/I18nContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useSnippetPlayer } from '@/lib/snippetPlayer';
import {
  getConversationsOverview,
  type ConversationsOverview as Overview,
  type OverviewBar,
  type OverviewClip,
  type OverviewProblem,
  type OverviewValue,
} from '@/api/conversations';
import { Tooltip } from '@/components/ui/tooltip';

/**
 * «Сводка» над списком разговоров (владелец, 16.09 AB8, AC1–AC13).
 *
 * Бары не знают, про что они: бэкенд отдаёт их из пресета (plugins/speech_to_text/
 * summary.py) — заголовок, значения, их тон, число и примеры. Поэтому новая сеть
 * или параметры, заданные пользователем, появятся здесь без правки интерфейса.
 * Цвет — по тону значения (хорошо / слабо / нейтрально / плохо), оттенок — по
 * порядку внутри тона.
 *
 * На каждом значении и каждой проблеме — карточка с примерами: реплики, кнопка
 * прослушать отрезок и кнопка открыть разговор целиком (владелец: «examples-
 * examples-examples, transcripts and audible»).
 */

const TONES: Record<OverviewValue['tone'], string[]> = {
  // Два «хороших» значения подряд (позиция интереснее / еда + напиток) сливались
  // одним зелёным — второй оттенок бирюзовый (Алексей 12.10, AI254).
  good: ['bg-emerald-500', 'bg-teal-400', 'bg-emerald-200'],
  weak: ['bg-amber-400', 'bg-amber-200', 'bg-amber-100'],
  neutral: ['bg-slate-300', 'bg-slate-200', 'bg-slate-100'],
  bad: ['bg-rose-400', 'bg-rose-300', 'bg-rose-200'],
};

function colors(values: OverviewValue[]): string[] {
  const used: Record<string, number> = {};
  return values.map((v) => {
    const k = used[v.tone] ?? 0;
    used[v.tone] = k + 1;
    const pal = TONES[v.tone] ?? TONES.neutral;
    return pal[Math.min(k, pal.length - 1)];
  });
}

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

type OpenFn = (conversationId: number) => void;
/** Особый запрос-чип в строку поиска: «проблема: нет товара», «общение: сухое» (AI19). */
type FacetFn = (query: string) => void;

function useWhen() {
  const { currentZone } = useCurrentZone();
  return (iso: string | null) => (iso
    ? new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      timeZone: currentZone?.timezone || undefined,
    }) : '');
}

/** Цвет заголовка подсказки — тон сегмента: «позиция интереснее» читается как
 *  зелёный заголовок, а не как строка списка (владелец, 16.09 п. 31). */
const TONE_TEXT: Record<OverviewValue['tone'], string> = {
  good: 'text-emerald-600', weak: 'text-amber-600', neutral: 'text-slate-600', bad: 'text-rose-600',
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80">{children}</div>
);

/** Реплики примера: «кто» колонкой, текст с одного места, мелко и без выделения —
 *  подсказка про число, транскрипция лишь подтверждает его (п. 32). */
function ClipLines({ lines }: { lines: OverviewClip['lines'] }) {
  const { t } = useT();
  return (
    // «Сотрудник»/«Гость» — колонкой по ЛЕВОМУ краю: выключка вправо рвала
    // столбик и выглядела грязно (Алексей 17.09, AI17.8).
    <div className="grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-1 text-[11px] leading-snug">
      {lines.map((l, i) => (
        <Fragment key={i}>
          <span className={`text-left text-[10px] ${l.speaker === 'cashier' ? 'text-primary/70' : 'text-muted-foreground/70'}`}>
            {t(l.speaker === 'cashier' ? 'conversations.dialogue.cashier' : 'conversations.dialogue.guest')}
          </span>
          <span className={l.hit ? 'text-foreground/90' : 'text-muted-foreground'}>{l.text}</span>
        </Fragment>
      ))}
    </div>
  );
}

/** Пример: «прослушать», время, «к разговору» и реплики. Описания разговора здесь
 *  нет — только то, что прозвучало (п. 35). */
export function ClipExample({ clip, onOpen, children }: { clip: OverviewClip; onOpen: OpenFn; children?: React.ReactNode }) {
  const { t } = useT();
  const when = useWhen();
  const { playingKey, loading, play } = useSnippetPlayer();
  const key = `${clip.conversation_id}:${clip.t}`;
  const on = playingKey === key;
  return (
    <div className="space-y-1 py-1.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Tooltip content={t(on ? 'conversations.overview.stop' : 'conversations.overview.play')}><button
          type="button"
          onClick={() => play(key, clip.conversation_id, clip.offset + clip.t, clip.dur)}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20"
          aria-label={t(on ? 'conversations.overview.stop' : 'conversations.overview.play')}
        >
          {on && loading ? <Loader2 className="size-3 animate-spin" />
            : on ? <Square className="size-2.5 fill-current" /> : <Play className="size-3 fill-current" />}
        </button></Tooltip>
        <span className="font-mono tabular-nums">{when(clip.started_at)}</span>
        <button
          type="button"
          onClick={() => onOpen(clip.conversation_id)}
          className="ml-auto inline-flex items-center gap-0.5 rounded px-1 py-0.5 hover:bg-muted hover:text-foreground"
        >
          {t('conversations.overview.open')}
          <ArrowUpRight className="size-3" />
        </button>
      </div>
      {children}
      {clip.lines.length > 0 && <ClipLines lines={clip.lines} />}
    </div>
  );
}

/** Шапка листания: заголовок секции и ‹ n/N › — примеры по одному, чтобы карточка
 *  не росла со списком и листалась пальцем (п. 33). */
function PagerHead({ title, index, count, setIndex }: { title: string; index: number; count: number; setIndex: (i: number) => void }) {
  return (
    <div className="flex items-center justify-between">
      <SectionTitle>{title}</SectionTitle>
      {count > 1 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
          <button type="button" disabled={index <= 0} onClick={() => setIndex(index - 1)}
            className="rounded p-0.5 hover:bg-muted disabled:opacity-40" aria-label="←">
            <ChevronLeft className="size-3" />
          </button>
          <span className="tabular-nums">{index + 1}/{count}</span>
          <button type="button" disabled={index >= count - 1} onClick={() => setIndex(index + 1)}
            className="rounded p-0.5 hover:bg-muted disabled:opacity-40" aria-label="→">
            <ChevronRight className="size-3" />
          </button>
        </span>
      )}
    </div>
  );
}

function ClipPager({ clips, onOpen }: { clips: OverviewClip[]; onOpen: OpenFn }) {
  const { t } = useT();
  const [i, setI] = useState(0);
  if (clips.length === 0) return null;
  const cur = Math.min(i, clips.length - 1);
  const c = clips[cur];
  return (
    <div className="mt-1.5 border-t pt-1">
      <PagerHead title={t('conversations.overview.examples')} index={cur} count={clips.length} setIndex={setI} />
      <ClipExample key={`${c.conversation_id}-${c.t}`} clip={c} onOpen={onOpen} />
    </div>
  );
}

/** Сколько формулировок показываем своим оттенком в сегменте; остальное — «прочее». */
const SUB_MAX = 6;

/** Под-сегменты значения по формулировкам («что предлагали»): доля каждой — по её
 *  счётчику, остаток — «прочее». Оттенок — тот же цвет с убывающей плотностью. */
function subSegments(v: OverviewValue): { text: string | null; n: number; opacity: number }[] {
  if (!v.examples.length) return [{ text: null, n: v.count, opacity: 1 }];
  const top = v.examples.slice(0, SUB_MAX);
  const used = top.reduce((sum, [, n]) => sum + n, 0);
  const subs: { text: string | null; n: number; opacity: number }[] = top.map(([text, n], i) => ({ text, n, opacity: Math.max(0.35, 1 - i * 0.12) }));
  const rest = Math.max(0, v.count - used);
  if (rest > 0) subs.push({ text: null, n: rest, opacity: 0.22 });
  return subs;
}

const subKey = (value: string, text: string | null) => `${value}|${text ?? ''}`;

/** Подсказка значения: заголовок цветом сегмента, доля, формулировки суб-легендой
 *  (оттенок под-сегмента, hover приподнимает его в баре, клик — чип в поиск, #1404),
 *  примеры по одному. Один стиль для всех баров (п. 34). */
function ValueCard({ v, total, onOpen, colorClass, onHover, onPick, onPickAll }: {
  v: OverviewValue; total: number; onOpen: OpenFn; colorClass?: string;
  onHover?: (text: string | null, on: boolean) => void; onPick?: (text: string) => void;
  /** «Прочее» — формулировки без своего оттенка: клик ставит в поиск само значение (AI254). */
  onPickAll?: () => void;
}) {
  const { t } = useT();
  const subs = subSegments(v);
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-semibold ${TONE_TEXT[v.tone] ?? TONE_TEXT.neutral}`}>{v.label}</span>
        <span className="tabular-nums text-muted-foreground">{pct(v.count, total)}% · {v.count}</span>
      </div>
      {v.note && <div className="text-muted-foreground">{v.note}</div>}
      {v.examples.length > 0 && (
        <div className="mt-1.5">
          <SectionTitle>{v.examples_title || t('conversations.overview.phrases')}</SectionTitle>
          <ul className="mt-0.5 space-y-0.5">
            {subs.map((sub) => (
              <li key={sub.text ?? '__rest'}>
                <Tooltip content={sub.text != null ? t('conversations.overview.pickPhrase') : t('conversations.overview.pickValue')}><button
                  type="button"
                  disabled={sub.text != null ? !onPick : !onPickAll}
                  onMouseEnter={() => onHover?.(sub.text, true)}
                  onMouseLeave={() => onHover?.(sub.text, false)}
                  onClick={() => (sub.text != null ? onPick?.(sub.text) : onPickAll?.())}
                  className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-foreground/80 enabled:hover:bg-muted enabled:hover:text-foreground disabled:cursor-default"
                >
                  <span className={cn('size-2 shrink-0 rounded-sm', colorClass ?? 'bg-slate-300')} style={{ opacity: sub.opacity }} />
                  <span className="min-w-0 flex-1 truncate">{sub.text != null ? `«${sub.text}»` : t('conversations.overview.otherPhrases')}</span>
                  <span className="tabular-nums text-muted-foreground">{sub.n > 1 || sub.text == null ? `×${sub.n}` : ''}</span>
                </button></Tooltip>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ClipPager clips={v.clips} onOpen={onOpen} />
    </div>
  );
}

function StackedBar({ bar, onOpen, onFacet }: { bar: OverviewBar; onOpen: OpenFn; onFacet?: FacetFn }) {
  const { t } = useT();
  const cls = colors(bar.values);
  const facetKind = bar.title.trim().toLowerCase();
  // Нулевые значения не показываем ни в полосе, ни в легенде (AC1).
  const shown = bar.values.map((v, i) => ({ v, c: cls[i] })).filter((x) => x.v.count > 0);
  // Под-сегмент, над формулировкой которого курсор в подсказке, — приподнят в баре (#1404).
  const [hot, setHot] = useState<string | null>(null);
  const card = (v: OverviewValue, c: string) => (
    <ValueCard v={v} total={bar.total} onOpen={onOpen} colorClass={c}
      onHover={(text, on) => setHot(on ? subKey(v.value, text) : null)}
      onPick={onFacet ? (text) => onFacet(`${facetKind}: ${v.label}: ${text}`) : undefined}
      onPickAll={onFacet ? () => onFacet(`${facetKind}: ${v.label}`) : undefined} />
  );
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-baseline justify-between gap-2 text-[12px]">
        <span className="font-medium text-foreground/90">{bar.title}</span>
        <span className="tabular-nums text-muted-foreground">{t('conversations.overview.of', { n: String(bar.total) })}</span>
      </div>
      {/* overflow-visible: приподнятый под-сегмент выходит за полосу; скругление — на крайних. */}
      <div className="flex h-2.5 w-full rounded-full bg-muted">
        {/* Клик по доле — фильтр ленты особым запросом «общение: сухое» (AI19).
            Раньше он висел только на баре «Общение», и по апселлу с лояльностью
            нажатие не делало ничего (Алексей 21.09, AI95). Вид запроса — это
            заголовок бара, ровно его же ждёт бэкенд (summary.facet_table).
            Сегмент бьётся на оттенки по формулировкам (#1404). */}
        {shown.map(({ v, c }, vi) => (
          <HoverCard key={v.value} content={card(v, c)}
            onClick={onFacet ? () => onFacet(`${facetKind}: ${v.label}`) : undefined}
            // Наведение на сам сегмент — приподнимается целиком; под-сегмент из
            // подсказки поверх этого приподнимается ещё (Алексей 12.10, AI254).
            className={cn('flex h-full cursor-pointer transition-transform duration-150 hover:z-10 hover:scale-y-[1.4]', vi === 0 && 'overflow-hidden rounded-l-full', vi === shown.length - 1 && 'overflow-hidden rounded-r-full')}
            style={{ width: `${(v.count / Math.max(1, bar.total)) * 100}%` }}>
            {subSegments(v).map((sub) => (
              <span key={sub.text ?? '__rest'} className={cn('h-full transition-transform duration-150', c, hot === subKey(v.value, sub.text) && 'scale-y-[1.8]')}
                style={{ width: `${(sub.n / Math.max(1, v.count)) * 100}%`, opacity: sub.opacity }} />
            ))}
          </HoverCard>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {shown.map(({ v, c }) => (
          <HoverCard key={v.value} content={card(v, c)}
            onClick={onFacet ? () => onFacet(`${facetKind}: ${v.label}`) : undefined}
            className="inline-flex cursor-pointer items-center gap-1 hover:text-foreground">
            <span className={`size-2 rounded-full ${c}`} />
            {v.label}
            <span className="tabular-nums text-foreground/70">{pct(v.count, bar.total)}%</span>
          </HoverCard>
        ))}
      </div>
    </div>
  );
}

const KIND_BAR: Record<string, string> = {
  out_of_stock: 'bg-orange-500', product: 'bg-amber-300', dialogue: 'bg-sky-400', cafe: 'bg-violet-400',
};

/** Карточка одной проблемы: что случилось, просили / ответили, пример со звуком.
 *  Важность цветом не выделяем — в подсказке это шум (п. 37). */
function ProblemCard({ p, onOpen }: { p: OverviewProblem; onOpen: OpenFn }) {
  const { t } = useT();
  const head = (
    <div className="space-y-0.5">
      <div className="text-foreground/90">
        {p.subject && <span className="text-foreground">{p.subject}: </span>}
        {p.text}
      </div>
      {(p.requested || p.answer) && (
        <div className="grid grid-cols-[auto_1fr] gap-x-2 text-[11px] text-muted-foreground">
          {p.requested && <><span>{t('conversations.overview.requested')}</span><span className="text-foreground/80">{p.requested}</span></>}
          {p.answer && <><span>{t('conversations.overview.answer')}</span><span className="text-foreground/80">{p.answer}</span></>}
        </div>
      )}
    </div>
  );
  if (!p.clip) {
    return (
      <div className="py-1.5">
        {head}
        <button type="button" onClick={() => onOpen(p.conversation_id)}
          className="mt-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">
          {t('conversations.overview.open')}
          <ArrowUpRight className="size-3" />
        </button>
      </div>
    );
  }
  return <ClipExample clip={p.clip} onOpen={onOpen}>{head}</ClipExample>;
}

/** Подсказка вида проблем: заголовок сверху, чего не было, примеры по одному (п. 37). */
function KindCard({ kind, items, onOpen }: {
  kind: Overview['problems']['kinds'][number];
  items: OverviewProblem[];
  onOpen: OpenFn;
}) {
  const { t } = useT();
  const [i, setI] = useState(0);
  const cur = Math.min(i, Math.max(0, items.length - 1));
  const p = items[cur];
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-semibold text-foreground">{kind.label}</span>
        <span className="tabular-nums text-muted-foreground">{kind.count}</span>
      </div>
      {kind.subjects.length > 0 && (
        <div className="mt-1.5">
          {kind.value === 'out_of_stock' && <SectionTitle>{t('conversations.overview.missingGoods')}</SectionTitle>}
          <div className="text-foreground/80">
            {kind.subjects.map(([s, n]) => `${s}${n > 1 ? ` ×${n}` : ''}`).join(', ')}
          </div>
        </div>
      )}
      {p && (
        <div className="mt-1.5 border-t pt-1">
          <PagerHead title={t('conversations.overview.examples')} index={cur} count={items.length} setIndex={setI} />
          <ProblemCard key={`${p.conversation_id}-${cur}`} p={p} onOpen={onOpen} />
        </div>
      )}
    </div>
  );
}

const PAGE = 8;

/** Проблемы — такой же столбик, как бары (AC4): полоса по видам с легендой; список
 *  открыт сразу, кнопка его сворачивает (п. 38). */
function ProblemsBar({ data, onOpen, onFacet, listOpen, setListOpen }: {
  data: Overview['problems'];
  onOpen: OpenFn;
  onFacet?: FacetFn;
  listOpen: boolean;
  setListOpen: (v: boolean) => void;
}) {
  const { t } = useT();
  const byKind = (k: string) => data.items.filter((p) => p.kind === k);
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-baseline justify-between gap-2 text-[12px]">
        <span className="font-medium text-foreground/90">{t('conversations.overview.problems')}</span>
        {data.total > 0 && (
          <button type="button" onClick={() => setListOpen(!listOpen)}
            className="inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground">
            {listOpen ? t('conversations.overview.hideList') : t('conversations.overview.list', { n: String(data.total) })}
            <ChevronDown className={`size-3 transition-transform ${listOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {data.total === 0 ? (
        <div className="text-[12px] text-muted-foreground">{t('conversations.overview.noProblems')}</div>
      ) : (
        <>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
            {data.kinds.map((k) => (
              <HoverCard key={k.value} content={<KindCard kind={k} items={byKind(k.value)} onOpen={onOpen} />}
                onClick={onFacet ? () => onFacet(`проблема: ${k.label}`) : undefined}
                className={`h-full cursor-pointer ${KIND_BAR[k.value] ?? 'bg-slate-300'}`}
                style={{ width: `${(k.count / data.total) * 100}%` }} />
            ))}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {data.kinds.map((k) => (
              <HoverCard key={k.value} content={<KindCard kind={k} items={byKind(k.value)} onOpen={onOpen} />}
                onClick={onFacet ? () => onFacet(`проблема: ${k.label}`) : undefined}
                className="inline-flex cursor-pointer items-center gap-1 hover:text-foreground">
                <span className={`size-2 rounded-full ${KIND_BAR[k.value] ?? 'bg-slate-300'}`} />
                {k.label}
                <span className="tabular-nums text-foreground/70">{k.count}</span>
              </HoverCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Список проблем: фильтр по виду, строки без цвета важности, вместо страниц — одна
 *  неприметная «показать ещё N» (п. 37/38). */
function ProblemList({ data, onOpen, onFacet }: { data: Overview['problems']; onOpen: OpenFn; onFacet?: FacetFn }) {
  const { t } = useT();
  const when = useWhen();
  const [kind, setKind] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const items = useMemo(() => data.items.filter((p) => kind == null || p.kind === kind), [data.items, kind]);
  // Группируем одинаковое: сначала по предмету («блендер», «раф»), иначе по
  // первым словам текста — «сломался блендер ×3» вместо трёх строк.
  const groups = useMemo(() => {
    type Group = { key: string; kind: string; label: string; items: OverviewProblem[] };
    const map = new Map<string, Group>();
    for (const p of items) {
      const subject = (p.subject || '').trim().toLowerCase();
      const words = (p.text || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
      const key = `${p.kind}|${subject || words.slice(0, 4).join(' ')}`;
      const label = p.subject?.trim() || p.text;
      const g: Group = map.get(key) ?? { key, kind: p.kind ?? '', label, items: [] };
      g.items.push(p);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => b.items.length - a.items.length);
  }, [items]);
  const pick = (k: string | null) => { setKind(k); setShown(PAGE); setOpenKey(null); };
  const rest = groups.length - shown;
  return (
    <div className="border-t pt-2">
      {/* Без заголовка ряд «все 18 · нет товара 13 · в общении 1» был непонятен (Алексей 25.10, #2376). */}
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Типы проблем</div>
      <div className="flex flex-wrap gap-1 text-[11px]">
        {/* «Все» — это сброс фильтра, а не ещё одно значение: значение «без
            категории» на все проблемы сразу читалось как фильтр и не снималось
            (владелец, 16.09 AC14). */}
        <button type="button" onClick={() => pick(null)}
          className={`rounded px-1.5 py-0.5 ${kind == null ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
          {t('conversations.overview.all')} <span className="tabular-nums">{data.total}</span>
        </button>
        {data.kinds.map((k) => (
          <button key={k.value} type="button" onClick={() => pick(kind === k.value ? null : k.value)}
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${kind === k.value ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
            <span className={`size-2 rounded-full ${KIND_BAR[k.value] ?? 'bg-slate-300'}`} />
            {k.label} <span className="tabular-nums">{k.count}</span>
          </button>
        ))}
      </div>
      {/* Одинаковые проблемы — одним чипом с ×N (Алексей 17.09, AI17.8): список
          пустых строчек читался хуже, чем «сломался блендер ×3» с раскрытием.
          Клик по чипу с одной проблемой уводит прямо к разговору. */}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {groups.slice(0, shown).map((g) => {
          const single = g.items.length === 1;
          const opened = openKey === g.key;
          return (
            <HoverCard
              key={g.key}
              as="span"
              content={<ProblemCard p={g.items[0]} onOpen={onOpen} />}
            >
              <button
                type="button"
                onClick={() => {
                  if (single) return onOpen(g.items[0].conversation_id);
                  // Несколько одинаковых — фильтруем ленту по предмету проблемы,
                  // а раскрытие оставляем вторым кликом (AI19).
                  if (onFacet && g.items[0].subject) return onFacet(`проблема: ${g.items[0].subject}`);
                  return setOpenKey(opened ? null : g.key);
                }}
                className={cn(
                  'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors',
                  opened ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border hover:bg-muted',
                )}
              >
                <span className={cn('size-2 shrink-0 rounded-full', KIND_BAR[g.kind] ?? 'bg-slate-300')} />
                <span className="min-w-0 truncate">{g.label}</span>
                {!single && <span className="tabular-nums text-muted-foreground">×{g.items.length}</span>}
                {single ? <ArrowUpRight className="size-3 shrink-0 text-muted-foreground" />
                  : <ChevronDown className={cn('size-3 shrink-0 text-muted-foreground transition-transform', opened && 'rotate-180')} />}
              </button>
            </HoverCard>
          );
        })}
      </div>
      {openKey && (
        <ul className="mt-1 divide-y divide-border/60">
          {(groups.find((g) => g.key === openKey)?.items ?? []).map((p, i) => (
            <li
              key={`${p.conversation_id}-${i}`}
              onClick={() => onOpen(p.conversation_id)}
              className="flex cursor-pointer items-baseline gap-2 py-1 text-[12px] hover:bg-muted/40"
            >
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{when(p.started_at)}</span>
              <span className="min-w-0 flex-1 truncate text-foreground/90">{p.text}</span>
              <ArrowUpRight className="size-3 shrink-0 text-muted-foreground" />
            </li>
          ))}
        </ul>
      )}
      {rest > 0 && (
        <button type="button" onClick={() => setShown(shown + PAGE)}
          className="mt-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">
          {t('conversations.overview.showMore', { n: String(Math.min(PAGE, rest)) })}
          <ChevronDown className="size-3" />
        </button>
      )}
    </div>
  );
}

export default function ConversationsOverview({ zoneId, from, to, onOpen, onFacet, onData }: {
  zoneId: string;
  from: string;
  to: string;
  /** Открыть разговор целиком в списке ниже (AC12). */
  onOpen: OpenFn;
  /** Поставить особый запрос в строку поиска: «проблема: …», «общение: …» (AI19). */
  onFacet?: FacetFn;
  /** Сводка загрузилась — конструктору чипов в поиске нужны бары и виды проблем (#1404). */
  onData?: (d: Overview | null) => void;
}) {
  const { t } = useT();
  const { data, error, isLoading } = useSWR(
    zoneId ? ['conversations-overview', zoneId, from, to] : null,
    () => getConversationsOverview(Number(zoneId), from, to),
    { revalidateOnFocus: false },
  );
  useEffect(() => { onData?.(data ?? null); }, [data, onData]);
  // Список проблем виден сразу — кнопку раскрытия не находили (п. 38).
  const [listOpen, setListOpen] = useState(true);
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2 border-b px-4 py-2.5">
        <div className="text-sm font-medium">{t('conversations.overview.title')}</div>
        {data && (
          <div className="text-xs text-muted-foreground">{t('conversations.overview.analyzed', { n: String(data.analyzed) })}</div>
        )}
      </div>
      {isLoading && !data ? (
        <div className="flex items-center justify-center gap-2 px-4 py-5 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading')}
        </div>
      ) : error && !data ? (
        <ServerStatusNotice variant="card" error={error} />
      ) : data ? (
        <div className="space-y-3 px-4 py-3">
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            {data.bars.map((b) => <StackedBar key={b.id} bar={b} onOpen={onOpen} onFacet={onFacet} />)}
            <ProblemsBar data={data.problems} onOpen={onOpen} onFacet={onFacet} listOpen={listOpen} setListOpen={setListOpen} />
          </div>
          {listOpen && data.problems.total > 0 && <ProblemList data={data.problems} onOpen={onOpen} onFacet={onFacet} />}
        </div>
      ) : null}
    </Card>
  );
}
